import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { countBy, compact, isEmpty, mapValues, noop } from 'lodash-es';
import { Page, Button, TextField, Typography, TableList, ChipButton, DropdownMenu, useDevice, useStateInRef, PageEmptyState, PageErrorState, TableListActions, ConfirmationModal, PageContentHeader, FilterPickerGroup } from 'seagull';
import { ChevronDown, ChevronUp, Edit, Eye, Trash } from 'seagull/icons';
import { DocumentNormal, EditSquare } from 'seagull/icons/content-edit';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { ClientPreview } from '~/components/ClientPreview/ClientPreview';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import PageLoadingState from '~/components/PageLoadingState';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import TableFilters from '~/components/TableFilters/TableFilters';
import { openInNewTabProps, routes } from '~/constants';
import { useEventReporter, useOverlayContext, useIoContext, useStore, useStoreActions } from '~/contexts';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useBulkActionModal } from '~/hooks/useTerminateAgreement/useBulkActionModal';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { ServerError } from '~/infra/ErrorResponse';
import { handlePromise } from '~/infra/utils';
import { ProposalTemplateSummaryModel, proposalTemplateModel } from '~/models';
import { ContactModel } from '~/models/ContactModel';
import { selectors } from '~/store/state-selectors';
import { helpArticles } from '~/utils/helpArticles';
import pluralize from '~/utils/pluralize';
import { NoTemplatesIcon } from './NoTemplatesIcon/NoTemplatesIcon';
import styles from './ProposalTemplates.module.scss';
const pageTitle = 'Proposal templates';
export var HeaderColId = /*#__PURE__*/ function(HeaderColId) {
    HeaderColId["name"] = "name";
    HeaderColId["lastUpdatedOn"] = "lastUpdatedOn";
    return HeaderColId;
}({});
const lastUpdatedOnLabel = 'Last updated on';
const templateNameLabel = 'Template name';
function getTableDefs() {
    const defs = compact([
        {
            headerItem: {
                id: "name",
                value: templateNameLabel
            },
            columnDef: {
                width: [
                    180,
                    500
                ],
                sortable: true,
                mobile: {
                    value: {
                        variant: 'paragraph1',
                        skin: 'standard',
                        weight: 'normal'
                    }
                }
            }
        },
        {
            headerItem: {
                id: "lastUpdatedOn",
                value: lastUpdatedOnLabel
            },
            columnDef: {
                width: [
                    180,
                    500
                ],
                sortable: true,
                mobile: {
                    label: {
                        text: lastUpdatedOnLabel,
                        hideColon: true
                    },
                    value: {
                        skin: 'secondary'
                    }
                }
            }
        }
    ]);
    return {
        columnDefs: defs.map((d)=>d.columnDef),
        headerItems: defs.map((d)=>d.headerItem)
    };
}
const sortFunctions = {
    ["name"]: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = ProposalTemplateSummaryModel.sortByTitle(a, b) || sortById(a, b);
            return isAscending ? retval : -retval;
        };
    },
    ["lastUpdatedOn"]: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortByLastUpdatedOnDate(a, b) || sortById(a, b);
            return isAscending ? retval : -retval;
        };
    }
};
const sortById = (a, b)=>a.id < b.id ? -1 : 1;
function sortByLastUpdatedOnDate(a, b) {
    return a.lastUpdatedOn() - b.lastUpdatedOn();
}
const sortOptions = [
    {
        id: "name",
        label: templateNameLabel,
        defaultDirection: 'asc',
        colIndex: 0
    },
    {
        id: "lastUpdatedOn",
        label: lastUpdatedOnLabel,
        defaultDirection: 'desc',
        colIndex: 1
    }
];
function sortProposalTemplates(config) {
    const { templates, sort, headerItems } = config;
    const result = templates.slice();
    if (sort) {
        var _headerItems_sort_colIndex;
        const colId = (_headerItems_sort_colIndex = headerItems[sort.colIndex]) === null || _headerItems_sort_colIndex === void 0 ? void 0 : _headerItems_sort_colIndex.id;
        if (colId) result.sort(sortFunctions[colId]({
            isAscending: sort.sort === 'asc'
        }));
    }
    return result;
}
function CreateTemplateButton() {
    const { reportButtonClick } = useEventReporter();
    const history = useHistory();
    const { t: commonTexts } = useLocaleKeys('common');
    return /*#__PURE__*/ _jsx(Button, {
        testId: "proposal-templates-empty-new-btn",
        intent: Intent.CreateProposalTemplate,
        onClick: ()=>{
            reportButtonClick('create-template');
            history.push(routes.proposalTemplateStudio);
        },
        children: commonTexts.buttons.createTemplate()
    });
}
function EmptyState(param) {
    let { isRefreshing } = param;
    const { t: texts } = useLocaleKeys('pages.proposalTemplates');
    const { t: commonTexts } = useLocaleKeys('common');
    return /*#__PURE__*/ _jsx(Page, {
        header: pageTitle,
        loading: isRefreshing,
        variant: "containedHeight",
        children: /*#__PURE__*/ _jsx(PageEmptyState, {
            maxHeight: true,
            testId: "proposal-templates-empty",
            illustration: /*#__PURE__*/ _jsx(NoTemplatesIcon, {}),
            title: texts.emptyTitle(),
            message: texts.emptyTip(),
            actions: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({
                        as: "a",
                        skin: "primaryOutline",
                        testId: "proposal-templates-learn-more",
                        href: helpArticles.proposalTemplates()
                    }, openInNewTabProps), {
                        children: commonTexts.buttons.learnMore()
                    })),
                    /*#__PURE__*/ _jsx(CreateTemplateButton, {})
                ]
            })
        })
    });
}
function ProposalTemplatesRow(props) {
    const { template, onUseTemplate, onPreview, onEdit, onRename, onRemove, editLabel, useTemplateLabel, previewLabel, renameLabel, removeLabel } = props;
    const testId = 'proposal-templates-row';
    const heapId = 'proposal-templates-row';
    const items = compact([
        {
            value: template.title,
            testId: 'proposal-template-name',
            variant: 'paragraph1',
            skin: 'standard',
            weight: 'normal',
            ellipsis: true
        },
        {
            value: template.formattedLastUpdatedOn(),
            testId: 'proposal-template-last-updated-on',
            variant: 'paragraph1',
            skin: 'standard',
            ellipsis: true
        }
    ]);
    return /*#__PURE__*/ _jsx(TableList.Row, {
        items: items,
        rowId: template.id,
        testId: testId,
        "data-heapid": heapId,
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            testId: "proposal-templates-row-actions",
            menuHeapId: "proposal-templates-row-actions-menu-btn",
            buttonText: editLabel,
            buttonIcon: /*#__PURE__*/ _jsx(Edit, {}),
            secondaryButtonText: useTemplateLabel,
            secondaryButtonIcon: /*#__PURE__*/ _jsx(DocumentNormal, {}),
            onClickButton: ()=>onEdit(template),
            onClickSecondaryButton: ()=>onUseTemplate(template),
            buttonIntent: Intent.EditProposalTemplate,
            btnHeapId: "".concat(heapId, "-menu-edit-menu-action"),
            mobile: {
                title: template.title
            },
            children: [
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(Eye, {}),
                    testId: "".concat(testId, "-preview-menu-action"),
                    "data-heapid": "".concat(heapId, "-menu-preview-menu-action"),
                    onClick: ()=>onPreview(template),
                    children: previewLabel
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(EditSquare, {}),
                    testId: "".concat(testId, "-rename-menu-action"),
                    "data-heapid": "".concat(heapId, "-menu-rename-menu-action"),
                    onClick: ()=>onRename(template),
                    intent: Intent.RenameProposalTemplate,
                    children: renameLabel
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    skin: "error",
                    testId: "".concat(testId, "-remove-menu-action"),
                    "data-heapid": "".concat(heapId, "-menu-remove-menu-action"),
                    onClick: ()=>onRemove(template),
                    intent: Intent.RemoveProposalTemplate,
                    children: removeLabel
                })
            ]
        })
    });
}
function ProposalTemplatesTable(props) {
    const { proposalTemplates, legalTermsDocs: _legalTermsDocs, search, onChangeSearch, refresh } = props;
    const { t: texts } = useLocaleKeys('pages.proposalTemplates');
    const { t: commonTexts } = useLocaleKeys('common');
    const { api } = useStoreActions();
    const scrollElRef = useRef(null);
    const { isMobileDevice } = useDevice();
    const overlayContext = useOverlayContext();
    const history = useHistory();
    const { getNow } = useIoContext();
    const worksAt = useStore(selectors.user.business);
    const { doBulkAction } = useBulkActionModal();
    const { headerItems, columnDefs } = useMemo(()=>getTableDefs(), []);
    const sortIds = useMemo(()=>headerItems.map((item)=>item.id), [
        headerItems
    ]);
    const defaultSort = useMemo(()=>({
            colIndex: 1,
            sort: 'desc'
        }), []);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const [selected, setSelected] = useState({});
    const getSelected = useStateInRef(selected);
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const onSortChange = (sort)=>{
        changeSort(sort);
    };
    const sortedProposalTemplates = useMemo(()=>sortProposalTemplates({
            templates: proposalTemplates,
            sort,
            headerItems
        }), [
        sort,
        proposalTemplates,
        headerItems
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: sortedProposalTemplates,
        scrollElRef
    });
    const onToggleAllSelection = useCallback((selectAll)=>{
        setSelected(proposalTemplates.reduce((acc, template)=>Object.assign(acc, {
                [template.id]: selectAll
            }), {}));
    }, [
        proposalTemplates
    ]);
    function getSelectedProposalTemplates() {
        return Object.entries(selected).filter((param)=>{
            let [, isSelected] = param;
            return isSelected;
        }).map((param)=>{
            let [proposalTemplateId, _] = param;
            return proposalTemplateId;
        });
    }
    async function bulkRemove(selectedProposalTemplates) {
        await doBulkAction({
            bulkItemIds: selectedProposalTemplates,
            confirmation: {
                title: texts.removeTemplates(),
                primaryButtonLabel: texts.sendRequest(),
                content: /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            children: [
                                texts.bulkRemoveConfirmPrefix(),
                                /*#__PURE__*/ _jsx(Typography, {
                                    testId: "proposal-template-remove-modal-template-name",
                                    as: "span",
                                    weight: "bold",
                                    children: selectedProposalTemplates.length
                                }),
                                texts.bulkRemoveConfirmSuffix()
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            children: texts.actionCannotBeUndone()
                        })
                    ]
                })
            },
            progressTitle: texts.removeTemplates(),
            actionName: 'removed',
            itemName: 'template',
            canProcessItem: ()=>true,
            processSingleItem: (proposaltemplateId)=>handlePromise(api.proposalTemplates.remove(proposaltemplateId)),
            onDone: (failedProposalTemplateIds)=>{
                setSelected(failedProposalTemplateIds.reduce((acc, templateId)=>_object_spread_props(_object_spread({}, acc), {
                        [templateId]: true
                    }), {}));
            }
        });
        refresh();
    }
    const onUseTemplate = async (template)=>{
        const draftId = await api.proposalTemplates.createDraft(template.id);
        history.push(routes.draft(draftId));
    };
    const onPreview = async (summary)=>{
        const template = await api.proposalTemplates.get(summary.id);
        const draft = proposalTemplateModel.toPreviewData(template);
        const sampleContact = ContactModel.makeSample({
            createdAt: getNow(),
            vendorId: worksAt === null || worksAt === void 0 ? void 0 : worksAt.id
        });
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (props)=>/*#__PURE__*/ _jsx(ClientPreview, _object_spread({
                    type: "draft",
                    draftInfo: {
                        draft,
                        contactsMap: {},
                        sampleContact
                    },
                    entityContext: {
                        source: 'proposalTemplate',
                        id: summary.id
                    }
                }, props))
        });
    };
    const onEdit = (template)=>{
        history.push(routes.proposalTemplate(template.id));
    };
    const onRename = async (template)=>{
        const newName = await overlayContext.showModal({
            content: function RenameModal(param) {
                let { dismissModal } = param;
                const [newName, setNewName] = useState(template.title);
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    title: texts.renameTemplate(),
                    testId: "proposal-template-rename-modal",
                    content: /*#__PURE__*/ _jsx(_Fragment, {
                        children: /*#__PURE__*/ _jsx(TextField, {
                            collapseHelperText: false,
                            testId: "proposal-template-rename-modal-input",
                            name: templateNameLabel,
                            value: newName,
                            onChange: (e)=>{
                                setNewName(e.target.value);
                            }
                        })
                    }),
                    primaryButtonLabel: commonTexts.buttons.save(),
                    onPrimaryButtonClick: ()=>dismissModal(newName),
                    secondaryButtonLabel: commonTexts.buttons.cancel(),
                    onSecondaryButtonClick: ()=>dismissModal(null),
                    onCloseButtonClick: ()=>dismissModal(null)
                });
            }
        });
        if (newName) {
            api.proposalTemplates.rename(template.id, newName).then(()=>refresh()).catch((e)=>{
                if (e.response.data.error === ServerError.NameAlreadyExists) overlayContext.showErrorToast(commonTexts.toasts.templateNameAlreadyExists(), 'duplicate-template-name-error');
            });
        }
    };
    const onRemove = async (template)=>{
        const doRemove = await overlayContext.showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    title: texts.removeTemplate(),
                    skin: "danger",
                    textAlignCenter: false,
                    testId: "proposal-template-remove-modal",
                    content: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    texts.removeConfirmPrefix(),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        testId: "proposal-template-remove-modal-template-name",
                                        as: "span",
                                        weight: "bold",
                                        children: template.title
                                    }),
                                    texts.removeConfirmSuffix()
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                children: texts.actionCannotBeUndone()
                            })
                        ]
                    }),
                    primaryButtonLabel: commonTexts.buttons.removeTemplate(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    secondaryButtonLabel: commonTexts.buttons.cancel(),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    onCloseButtonClick: ()=>dismissModal(false)
                });
            }
        });
        if (doRemove) {
            api.proposalTemplates.remove(template.id).then(()=>refresh());
        }
    };
    useEffect(()=>{
        // this unselects any proposal template that is not found in the newly fetched list of proposal templates
        if (!proposalTemplates || isEmpty(getSelected())) return;
        const ids = proposalTemplates.map((proposalTemplate)=>proposalTemplate.id);
        const newSelected = mapValues(getSelected(), (origValue, proposalTemplateId)=>ids.includes(proposalTemplateId) ? origValue : false);
        setSelected(newSelected);
    }, [
        proposalTemplates
    ]);
    return /*#__PURE__*/ _jsx(Page, {
        testId: "proposal-templates-table",
        header: pageTitle,
        ref: scrollElRef,
        actions: /*#__PURE__*/ _jsx(CreateTemplateButton, {}),
        subheader: pluralize('templates', proposalTemplates.length, true),
        onScroll: onScroll,
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            hasActionColumn: true,
            enableRowSelection: true,
            columnDefs: columnDefs,
            selectedRows: selected,
            sort: sort,
            onSortChange: onSortChange,
            onSelectionChange: setSelected,
            numRows: proposalTemplates.length,
            numSelectedRows: numSelected,
            onToggleAllSelection: onToggleAllSelection,
            children: [
                /*#__PURE__*/ _jsxs(PageContentHeader, {
                    children: [
                        /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                            flexWrap: true,
                            testId: "proposal-templates-filter-bar",
                            style: {
                                marginBlockEnd: 24
                            },
                            children: [
                                /*#__PURE__*/ _jsx(TableFilters, {
                                    testIdPrefix: "proposal-templates-dashboard",
                                    filterLabels: {},
                                    filterConfig: {},
                                    selectedFilters: {},
                                    setSelectedFilters: noop,
                                    onRefresh: (_filters, newSort)=>{
                                        if (newSort) changeSort(newSort);
                                    },
                                    search: {
                                        value: search,
                                        testId: 'proposal-templates-search',
                                        registerHotkey: true,
                                        debounceTime: 500,
                                        onChange: (event)=>{
                                            const searchStr = event.target.value;
                                            onChangeSearch(searchStr);
                                        },
                                        onClear: ()=>onChangeSearch('')
                                    },
                                    mobile: {
                                        sortOptions
                                    }
                                }),
                                !isMobileDevice && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            className: styles.selectedItems,
                                            testId: "proposalTemplate-dashboard-bulk-counter",
                                            children: numSelected > 0 ? "".concat(pluralize('template', numSelected, true), " selected") : ''
                                        }),
                                        /*#__PURE__*/ _jsx(DropdownMenu, {
                                            onOpenChange: setBulkActionsOpen,
                                            target: /*#__PURE__*/ _jsx(ChipButton, {
                                                testId: "proposal-templates-bulk-actions",
                                                suffixIcon: bulkActionsOpen ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {}),
                                                highlight: bulkActionsOpen,
                                                disabled: numSelected === 0,
                                                children: texts.bulkActions()
                                            }),
                                            children: /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                                iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                                                skin: "error",
                                                intent: Intent.RemoveProposalTemplate,
                                                testId: "proposal-templates-bulk-remove",
                                                onClick: ()=>bulkRemove(getSelectedProposalTemplates()),
                                                children: commonTexts.buttons.remove()
                                            })
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(TableList.Header, {
                            items: headerItems,
                            testId: "proposal-templates-table-header"
                        })
                    ]
                }),
                paginatedData.map((proposalTemplate)=>/*#__PURE__*/ _jsx(ProposalTemplatesRow, {
                        template: proposalTemplate,
                        onUseTemplate: onUseTemplate,
                        onPreview: onPreview,
                        onEdit: onEdit,
                        onRename: onRename,
                        onRemove: onRemove,
                        editLabel: texts.edit(),
                        useTemplateLabel: commonTexts.buttons.useTemplate(),
                        previewLabel: commonTexts.buttons.preview(),
                        renameLabel: commonTexts.buttons.rename(),
                        removeLabel: commonTexts.buttons.remove()
                    }, proposalTemplate.id))
            ]
        })
    });
}
export function ProposalTemplates() {
    const { api, fetchLegalTerms } = useStoreActions();
    const shouldRefresh = useRef(false);
    const refreshRef = useRef(()=>{});
    const { urlState, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        }
    });
    const { search } = urlState;
    useSetDocumentTitle(pageTitle);
    useEffect(()=>{
        if (shouldRefresh.current) refreshRef.current();
    }, [
        urlState
    ]);
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: ()=>Promise.all([
                api.proposalTemplates.getAll().then((templateSummary)=>templateSummary.filter((t)=>t.lowercaseTitle().includes(search.toLowerCase()))),
                fetchLegalTerms().then((legalTerms)=>{
                    if (!legalTerms) throw makeSentryIgnoredError('legalTermsError');
                    return legalTerms;
                })
            ]).then((param)=>{
                let [proposalTemplates, legalTermsDocs] = param;
                return {
                    proposalTemplates,
                    legalTermsDocs
                };
            }),
        renderLoader: ()=>/*#__PURE__*/ _jsx(PageLoadingState, {
                header: pageTitle,
                loading: true
            }),
        renderError: (param)=>{
            let { refresh } = param;
            return /*#__PURE__*/ _jsx(PageErrorState, {
                title: pageTitle,
                onRefresh: refresh
            });
        },
        renderChild: (param)=>{
            let { data: { proposalTemplates, legalTermsDocs }, loading, refresh } = param;
            refreshRef.current = refresh;
            return proposalTemplates.length === 0 && !search ? /*#__PURE__*/ _jsx(EmptyState, {
                isRefreshing: loading
            }) : /*#__PURE__*/ _jsx(ProposalTemplatesTable, {
                proposalTemplates: proposalTemplates,
                legalTermsDocs: legalTermsDocs,
                refresh: refresh,
                isRefreshing: loading,
                search: search,
                onChangeSearch: (search)=>{
                    shouldRefresh.current = true;
                    changeUrlState({
                        search
                    });
                }
            });
        }
    });
}
